// Opening move: LyricsTextNode - TextNode replacement with NodeState-backed stress data
// This replaces Lexical's default TextNode to enable stress pattern storage without DOM changes
// Big play: Uses NodeState for headless data infrastructure following .local_docs/lexical/core-docs/01-nodes.md

import {
  $applyNodeReplacement,
  TextNode,
  type DOMConversionMap,
  type EditorConfig,
  type LexicalNode,
  type SerializedTextNode,
  type Spread,
} from 'lexical'
import { createState, $getState, $setState } from 'lexical'
import type {
  StressPattern,
  StressPatternState,
} from '../types/prosody'

// Import helper functions for use in node methods
import {
  createDefaultStressPatternState as _createDefaultStressPatternState,
  normalizeWordKey as _normalizeWordKey,
} from '../types/prosody'

/**
 * Serialized format for LyricsTextNode
 *
 * Time to tackle the tricky bit: Extends SerializedTextNode to preserve all base TextNode fields
 * Main play: stressPatternState only appears in JSON when it has data (NodeState omits defaults)
 */
export type SerializedLyricsTextNode = Spread<
  {
    stressPatternState?: StressPatternState
  },
  SerializedTextNode
>

/**
 * NodeState configuration for stress patterns
 *
 * Big play: Define the state key and parse function for NodeState integration
 * Victory lap: Parse function ensures we always get a valid object, even for undefined/null
 * Reference: .local_docs/lexical/core-docs/01-nodes.md - NodeState patterns
 */
const stressPatternState = createState<string, StressPatternState>('stressPatternState', {
  parse: (value: unknown): StressPatternState => {
    // Opening move: Validate and normalize the incoming value
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as StressPatternState
    }
    // Victory lap: Return empty default if value is invalid
    return _createDefaultStressPatternState()
  },
})

/**
 * LyricsTextNode - Replacement for Lexical's TextNode with stress data storage
 *
 * Main play: Extends TextNode to participate in all base text behaviors (formatting, modes, etc.)
 * Big play: Stores stress patterns via NodeState instead of custom properties
 * Time to tackle the tricky bit: Preserves flat-text DOM invariant - no spans, no attributes
 *
 * Critical constraints from iteration plan:
 * - MUST use NodeState, not custom properties
 * - MUST preserve flat-text DOM (element.textContent = this.getTextContent())
 * - MUST NOT add DOM attributes for stress data
 * - Serialization MUST omit default values from JSON
 *
 * Reference: .local_docs/lexical/core-docs/01-nodes.md - TextNode DOM structure requirements
 */
export class LyricsTextNode extends TextNode {
  static getType(): string {
    // CRITICAL: Must return unique type identifier (NOT 'text')
    // Reference: .local_docs/lexical/core-docs/01-nodes.md - Node Replacement Options
    // The replacement registration (withKlass) tells Lexical to treat this as TextNode
    return 'lyrics-text'
  }

  static clone(node: LyricsTextNode): LyricsTextNode {
    // Main play: Clone creates new instance with same text and key
    // Big play: NodeState is automatically cloned by Lexical's internal systems
    return new LyricsTextNode(node.__text, node.__key)
  }

  /**
   * Configure node with NodeState registration
   *
   * Time to tackle the tricky bit: Tell Lexical this node extends TextNode and uses stressPatternState
   * Main play: flat: true lifts state to top-level JSON during export
   * Reference: .local_docs/lexical/core-docs/01-nodes.md - $config() and pre-registered state
   */
  $config() {
    return this.config('lyrics-text', {
      extends: TextNode,
      stateConfigs: [{ flat: true, stateConfig: stressPatternState }],
    })
  }

  static importJSON(serializedNode: SerializedLyricsTextNode): LyricsTextNode {
    // Opening move: Create fresh node from base TextNode fields
    const node = $createLyricsTextNode(serializedNode.text || '')
    node.setFormat(serializedNode.format || 0)
    node.setDetail(serializedNode.detail || 0)
    if (serializedNode.mode !== undefined) {
      node.setMode(serializedNode.mode)
    }
    node.setStyle(serializedNode.style || '')

    // Big play: Restore stress pattern state if present in serialized data
    // Victory lap: If omitted (default value), NodeState automatically uses parse(undefined)
    if (serializedNode.stressPatternState) {
      $setState(node, stressPatternState, serializedNode.stressPatternState)
    }

    return node
  }

  exportJSON(): SerializedLyricsTextNode {
    // Opening move: Export all base TextNode fields via super
    const baseJson = super.exportJSON()

    // Main play: Get current stress pattern state from NodeState
    const currentState = $getState(this, stressPatternState)

    // Time to tackle the tricky bit: Only include stressPatternState if it has data
    // Victory lap: This keeps JSON lean - Lexical auto-omits defaults, but we're explicit for clarity
    const hasData = currentState && typeof currentState === 'object' && Object.keys(currentState).length > 0

    return {
      ...baseJson,
      type: 'lyrics-text', // CRITICAL: Must match getType() for serialization/import
      ...(hasData ? { stressPatternState: currentState } : {}),
    }
  }

  /**
   * Create DOM element for this node
   *
   * CRITICAL: Must preserve flat-text DOM invariant
   * Reference: .local_docs/lexical/core-docs/01-nodes.md - TextNode DOM Structure
   *
   * Big play: Delegate entirely to base TextNode - no custom wrapper, no attributes
   * Victory lap: DOM output identical to stock TextNode (acceptance criteria 1, 2, 9)
   * Time to tackle the tricky bit: Visual stress rendering happens elsewhere (future epic)
   */
  createDOM(config: EditorConfig): HTMLElement {
    // Opening move: Let base TextNode handle DOM creation completely
    // This ensures identical DOM structure to stock TextNode
    return super.createDOM(config)
  }

  /**
   * Update existing DOM element
   *
   * Big play: Delegate entirely to base TextNode - no custom update logic
   * Victory lap: Lexical handles textContent reconciliation automatically
   * Reference: .local_docs/lexical/core-docs/01-nodes.md - updateDOM lifecycle
   */
  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    // Opening move: Let base TextNode handle DOM updates completely
    return super.updateDOM(prevNode, dom, config)
  }

  static importDOM(): DOMConversionMap | null {
    // Main play: Import regular spans as LyricsTextNode to support paste/copy
    return {
      span: () => ({
        conversion: (element: HTMLElement) => {
          const node = $createLyricsTextNode(element.textContent || '')
          return { node }
        },
        priority: 0,
      }),
    }
  }
}

/**
 * Type guard for LyricsTextNode instances
 *
 * Victory lap: Standard Lexical pattern for type-safe node access
 */
export function $isLyricsTextNode(node: LexicalNode | null | undefined): node is LyricsTextNode {
  return node instanceof LyricsTextNode
}

/**
 * Factory function for creating LyricsTextNode instances
 *
 * Main play: Use $applyNodeReplacement to ensure editor node replacement config is respected
 * Reference: .local_docs/lexical/core-docs/01-nodes.md - Node Replacement Options
 */
export function $createLyricsTextNode(text = ''): LyricsTextNode {
  return $applyNodeReplacement(new LyricsTextNode(text))
}

// ============================================================================
// Stress Pattern Accessor Helpers
// ============================================================================

/**
 * Get stress pattern for a specific word from the node's state
 *
 * Opening move: Normalize word key and look up in NodeState
 * Big play: Returns null if no pattern exists for this word
 */
export function $getStressPattern(node: LyricsTextNode, word: string): StressPattern | null {
  const state = $getState(node, stressPatternState)
  if (!state || typeof state !== 'object') return null

  const key = _normalizeWordKey(word)
  return (state as StressPatternState)[key] || null
}

/**
 * Set stress pattern for a specific word in the node's state
 *
 * Time to tackle the tricky bit: Must clone state object before mutation (NodeState requirement)
 * Main play: Mark node dirty by creating new state object with updated pattern
 * Reference: .local_docs/lexical/core-docs/01-nodes.md - NodeState copy-on-write
 */
export function $setStressPattern(
  node: LyricsTextNode,
  word: string,
  pattern: StressPattern
): void {
  const currentState = $getState(node, stressPatternState) as StressPatternState
  const key = _normalizeWordKey(word)

  // Big play: Clone state object to trigger Lexical's dirty tracking
  const newState: StressPatternState = { ...currentState, [key]: pattern }
  $setState(node, stressPatternState, newState)
}

/**
 * Clear stress pattern for a specific word from the node's state
 *
 * Main play: Remove word key from state object
 * Victory lap: If state becomes empty, Lexical omits it from JSON export
 */
export function $clearStressPattern(node: LyricsTextNode, word: string): void {
  const currentState = $getState(node, stressPatternState) as StressPatternState
  const key = _normalizeWordKey(word)

  // Opening move: Check if key exists before cloning state
  if (!currentState[key]) {
    return // Nothing to clear
  }

  // Big play: Clone state and remove key
  const newState: StressPatternState = { ...currentState }
  delete newState[key]
  $setState(node, stressPatternState, newState)
}

/**
 * Check if node has any stress pattern data
 *
 * Victory lap: Useful for conditional logic in plugins and rendering
 */
export function $hasStressPattern(node: LyricsTextNode): boolean {
  const state = $getState(node, stressPatternState)
  return state && typeof state === 'object' && Object.keys(state).length > 0
}

/**
 * Get all stress patterns from the node's state
 *
 * Main play: Returns complete state object for bulk operations
 * Time to tackle the tricky bit: Returns read-only reference - use $setStressPattern to modify
 */
export function $getAllStressPatterns(node: LyricsTextNode): Readonly<StressPatternState> {
  return $getState(node, stressPatternState) as StressPatternState
}

/**
 * Check if a word's stress pattern was manually overridden
 *
 * Main play: Determines if pattern was set by user interaction or auto-analysis
 * Victory lap: Used by undo system to distinguish manual vs auto operations
 */
export function $isManualOverride(node: LyricsTextNode, word: string): boolean {
  const pattern = $getStressPattern(node, word)
  return pattern?.isManualOverride === true
}

/**
 * Clear manual override for a word, enabling auto pattern restoration
 *
 * Big play: REMOVES the pattern entirely so StressAutoLoadPlugin can re-analyze
 * Time to tackle the tricky bit: By clearing the pattern, the auto-load listener
 * will detect "no pattern exists" and queue a fresh backend call
 *
 * Note: The actual auto pattern restoration happens asynchronously via
 * CLEAR_STRESS_OVERRIDE_COMMAND which calls coordinator.analyzePassage()
 */
export function $clearManualOverride(node: LyricsTextNode, word: string): void {
  const pattern = $getStressPattern(node, word)
  if (!pattern) return

  // Remove pattern entirely - the command handler (CLEAR_STRESS_OVERRIDE_COMMAND)
  // will call coordinator.analyzePassage() to restore fresh auto pattern
  $clearStressPattern(node, word)
}

// Victory lap: LyricsTextNode complete with NodeState-backed stress infrastructure
// Preserves flat-text DOM, provides clean accessor helpers, ready for plugin integration
